import React, { useCallback, useContext, useState } from "react";
import { Dropdown, Tooltip, message } from "antd";
import { getUsageWidgetDetail } from "../../../apis/analyticsUsageApi";
import {
  audienceToUserExportSheet,
  exportSectionDashboardExcel,
  exportSectionUsersExcel,
  exportSheetExcel,
  hotLeadsToUserExportSheet,
  resolveWidgetExportKey,
} from "./analyticsExport";

export const AnalyticsExportContext = React.createContext(null);

const stopPanelAction = (e) => {
  e.stopPropagation();
};

const WidgetExportButton = ({
  sheet,
  slug,
  widgetKey,
  exportParams,
  hotLeadsRows,
  disabled = false,
  className = "",
}) => {
  const ctx = useContext(AnalyticsExportContext);
  const [userLoading, setUserLoading] = useState(false);

  const resolvedParams = exportParams || ctx?.params;
  const resolvedHotLeads = hotLeadsRows ?? ctx?.hotLeadsRows;
  const resolvedWidgetKey = widgetKey || slug;
  const hotLeadsExportOnly = hotLeadsRows !== undefined && hotLeadsRows !== null;
  const canSummary = Boolean(sheet?.rows?.length);
  const canUsers = Boolean(
    resolvedHotLeads?.length || (!hotLeadsExportOnly && resolvedWidgetKey && resolvedParams)
  );

  const handleUserExport = useCallback(async () => {
    setUserLoading(true);
    try {
      let userSheet = null;

      if (hotLeadsExportOnly) {
        userSheet = hotLeadsToUserExportSheet(
          resolvedHotLeads || [],
          sheet?.name ? `${sheet.name} Users` : undefined
        );
      } else if (resolvedHotLeads?.length) {
        userSheet = hotLeadsToUserExportSheet(
          resolvedHotLeads,
          sheet?.name ? `${sheet.name} Users` : undefined
        );
      } else if (resolvedWidgetKey && resolvedParams) {
        const res = await getUsageWidgetDetail({
          ...resolvedParams,
          widget: resolveWidgetExportKey(resolvedWidgetKey),
          export_users: "1",
        });
        userSheet = audienceToUserExportSheet(
          res?.result,
          sheet?.name ? `${sheet.name} Users` : `${resolveWidgetExportKey(resolvedWidgetKey)} Users`
        );
      }

      if (!userSheet?.rows?.length) {
        message.warning("No users found for this widget in the selected period.");
        return;
      }

      const fileSlug = `${slug || resolvedWidgetKey || "widget"}-users`;
      exportSheetExcel(userSheet, fileSlug);
      message.success(`Exported ${userSheet.rows.length} users`);
    } catch (err) {
      message.error(err?.message || "Failed to export users");
    } finally {
      setUserLoading(false);
    }
  }, [hotLeadsExportOnly, resolvedHotLeads, resolvedWidgetKey, resolvedParams, sheet?.name, slug]);

  if (disabled || (!canSummary && !canUsers)) return null;

  const items = [
    ...(canSummary
      ? [
          {
            key: "summary-xlsx",
            label: "Summary",
            onClick: () => exportSheetExcel(sheet, slug),
          },
        ]
      : []),
    ...(canUsers
      ? [
          {
            key: "users-xlsx",
            label: userLoading ? "Loading users…" : "Users",
            disabled: userLoading,
            onClick: handleUserExport,
          },
        ]
      : []),
  ];

  return (
    <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
      <Tooltip title="Export data">
        <button
          type="button"
          className={`analytics-export-btn ${userLoading ? "analytics-export-btn--loading" : ""} ${className}`.trim()}
          aria-label="Export widget data"
          onClick={stopPanelAction}
          onKeyDown={stopPanelAction}
          disabled={userLoading}
        >
          <i className={`fa-solid ${userLoading ? "fa-spinner fa-spin" : "fa-file-export"}`} aria-hidden="true" />
        </button>
      </Tooltip>
    </Dropdown>
  );
};

export const SectionExportButton = ({
  exportSheets,
  widgetKeys,
  sectionSlug,
  sectionLabel = "Section",
  exportParams,
  disabled = false,
  className = "",
}) => {
  const ctx = useContext(AnalyticsExportContext);
  const [loading, setLoading] = useState(false);
  const resolvedParams = exportParams || ctx?.params;
  const periodLabel = exportSheets?._meta?.periodLabel || "export";

  if (disabled || !widgetKeys?.length) return null;

  const handleSummaryExport = () => {
    const ok = exportSectionDashboardExcel(exportSheets, widgetKeys, sectionSlug, periodLabel);
    if (!ok) message.warning(`No ${sectionLabel} data to export.`);
  };

  const handleUsersExport = async () => {
    setLoading(true);
    const hide = message.loading(`Fetching ${sectionLabel} users…`, 0);
    try {
      const ok = await exportSectionUsersExcel({
        params: resolvedParams,
        exportSheets,
        widgetKeys,
        sectionSlug,
        fetchWidgetDetail: getUsageWidgetDetail,
      });
      hide();
      if (!ok) message.warning(`No user data to export for ${sectionLabel}.`);
      else message.success(`${sectionLabel} user export downloaded.`);
    } catch (err) {
      hide();
      message.error(err?.message || "Failed to export users");
    } finally {
      setLoading(false);
    }
  };

  const items = [
    {
      key: "section-xlsx",
      label: `Export ${sectionLabel}`,
      onClick: handleSummaryExport,
    },
    {
      key: "section-users-xlsx",
      label: loading ? "Loading users…" : `Export ${sectionLabel} users`,
      disabled: loading || !resolvedParams,
      onClick: handleUsersExport,
    },
  ];

  return (
    <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
      <Tooltip title={`Export ${sectionLabel}`}>
        <button
          type="button"
          className={`analytics-export-btn ${loading ? "analytics-export-btn--loading" : ""} ${className}`.trim()}
          aria-label={`Export ${sectionLabel}`}
          onClick={stopPanelAction}
          onKeyDown={stopPanelAction}
          disabled={loading}
        >
          <i className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-file-export"}`} aria-hidden="true" />
        </button>
      </Tooltip>
    </Dropdown>
  );
};

export default WidgetExportButton;
